import cn from 'classnames';
import React, { useEffect } from 'react';

import { track } from 'apis/amethyst';
import { evProductNoStyleSizes } from 'events/product';
import AirplaneButton from 'components/productdetail/stylepicker/AirplaneButton';
import { AirplaneButtonDimension, SetItemSelection } from 'components/productdetail/stylepicker/AirplaneSeatSizing';
import SizeUnitTabs from 'components/productdetail/stylepicker/SizeUnitTabs';
import { capitalize } from 'helpers/index';
import useMartyContext from 'hooks/useMartyContext';
import { AirplaneCache, AirplaneCacheDimensionOption, AirplaneCacheUnitOption } from 'types/AirplaneCache';
import { TestId } from 'types/app';

import css from 'styles/components/productdetail/stylepicker/airplaneFieldset.scss';

interface SizingUnitPicker {
  options: AirplaneCacheUnitOption[];
  selectedOption: AirplaneCacheUnitOption;
  setItemSelection: SetItemSelection;
}

export interface AirplaneFieldsetProps {
  airplaneCache: AirplaneCache;
  applyWarningStyles: boolean;
  fieldsetHasWarnings: boolean;
  isInStockFn?: (option: AirplaneCacheDimensionOption) => boolean;
  isSelectSizeTooltipVisible: boolean;
  isSelectedFn?: (option: AirplaneCacheDimensionOption) => boolean;
  legendId?: string;
  legendText: string;
  options: AirplaneCacheDimensionOption[];
  pickDisplayedOptions?: (options: AirplaneCacheDimensionOption[]) => AirplaneCacheDimensionOption[];
  radioButtonName: string;
  setItemSelection: SetItemSelection;
  sizingUnitPicker?: SizingUnitPicker;
  typeKey: AirplaneButtonDimension;
  useNarrowOptionButtons?: boolean;
  namespace: string;
  styleId: string;
  isHydraCantFindYourSize?: boolean;
}

/**
 * Creates the data-test-id for the gender, size, and width legends.
 *
 * Tags for people grepping the codebase:
 * airplaneSeatSizeSelectAgeGroupLegend, airplaneSeatSizeSelectAgeGroupLegendWarning,
 * airplaneSeatSizeSelectGenderLegend, airplaneSeatSizeSelectGenderLegendWarning,
 * airplaneSeatSizeSelectSingleShoeSideLegend, airplaneSeatSizeSelectSingleShoeSideLegendWarning,
 * airplaneSeatSizeSelectSizeLegend, airplaneSeatSizeSelectSizeLegendWarning,
 * airplaneSeatSizeSelectWidthLegend, airplaneSeatSizeSelectWidthLegendWarning,
 * airplaneSeatSizeSelectSizeRangeLegend
 */
export function makeLegendDataTestId(typeKey: AirplaneButtonDimension, isSelectSizeTooltipVisible: boolean) {
  const keyInTestId = capitalize(typeKey);
  if (isSelectSizeTooltipVisible) {
    return `airplaneSeatSizeSelect${keyInTestId}LegendWarning`;
  }
  return `airplaneSeatSizeSelect${keyInTestId}Legend`;
}

/**
 * Creates the data-test-id for the gender, size, and width option containers.
 *
 * Tags for people grepping the codebase:
 * airplaneSeatSizeOptionGroupForAgeGroup, airplaneSeatSizeOptionGroupForGender,
 * airplaneSeatSizeOptionGroupForSingleShoeSide,
 * airplaneSeatSizeOptionGroupForSize, airplaneSeatSizeOptionGroupForWidth
 */
export function makeOptionGroupDataTestId(typeKey: AirplaneButtonDimension) {
  const suffix = capitalize(typeKey);
  return `airplaneSeatSizeOptionGroupFor${suffix}`;
}

function makeSizingUnitPicker(sizingUnitPicker: SizingUnitPicker) {
  const { options, selectedOption, setItemSelection } = sizingUnitPicker;
  return (
    <SizeUnitTabs
      onChange={option => {
        const syntheticChangeEvent = {
          target: { id: option?.id || options[0]?.id }
        } as React.ChangeEvent<HTMLInputElement>;
        setItemSelection(syntheticChangeEvent);
      }}
      options={options}
      selectedOption={selectedOption}
    />
  );
}

function defaultIsInStock(typeKey: AirplaneButtonDimension, airplaneCache: Pick<AirplaneCache, 'available'>, option: AirplaneCacheDimensionOption) {
  const availableOptionsKey = `${typeKey}Options` as keyof AirplaneCache['available'];
  const availableOptions = airplaneCache.available[availableOptionsKey] as string[];
  const { constraintValue } = option;
  return availableOptions.includes(constraintValue);
}

function defaultIsSelected(
  typeKey: AirplaneButtonDimension,
  airplaneCache: Pick<AirplaneCache, 'constraints'>,
  option: AirplaneCacheDimensionOption
) {
  const { constraintValue } = option;
  const constraintName = typeKey as keyof AirplaneCache['constraints'];
  return constraintValue === airplaneCache.constraints[constraintName];
}

function makeOptions(displayedOptions: AirplaneCacheDimensionOption[], props: AirplaneFieldsetProps, testId: TestId): JSX.Element {
  const { airplaneCache, isInStockFn, isSelectedFn, radioButtonName, setItemSelection, typeKey, useNarrowOptionButtons, namespace } = props;

  if (!(displayedOptions.length > 0)) {
    return <p className={css.noOptions}>No options available. Please modify the selection above.</p>;
  }

  const optionGroupTestId = testId(makeOptionGroupDataTestId(typeKey));
  const optionsClassName = cn(css.airplaneSizingContainer, {
    [css.narrowOptionButtons]: useNarrowOptionButtons
  });

  return (
    <div className={optionsClassName} data-test-id={optionGroupTestId}>
      {displayedOptions.map(option => {
        const isInStock = isInStockFn ? isInStockFn(option) : defaultIsInStock(typeKey, airplaneCache, option);
        const isSelected = isSelectedFn ? isSelectedFn(option) : defaultIsSelected(typeKey, airplaneCache, option);
        return (
          <AirplaneButton
            isInStock={isInStock}
            isSelected={isSelected}
            key={option.id}
            option={option}
            radioButtonName={radioButtonName}
            setItemSelection={setItemSelection}
            typeKey={typeKey}
            namespace={namespace}
          />
        );
      })}
    </div>
  );
}

export default function AirplaneFieldset(props: AirplaneFieldsetProps) {
  const {
    applyWarningStyles,
    fieldsetHasWarnings,
    isSelectSizeTooltipVisible,
    legendId,
    legendText,
    options,
    pickDisplayedOptions,
    sizingUnitPicker,
    typeKey,
    namespace,
    styleId,
    isHydraCantFindYourSize
  } = props;
  const { testId } = useMartyContext();

  const legendTestId = testId(makeLegendDataTestId(typeKey, isSelectSizeTooltipVisible));
  const legendClassName = cn(css.title, {
    [css.warning]: fieldsetHasWarnings && applyWarningStyles
  });
  const displayedOptions = pickDisplayedOptions ? pickDisplayedOptions(options) : options;

  useEffect(() => {
    if (displayedOptions.length === 0) {
      track(() => [evProductNoStyleSizes, { productId: namespace, styleId }]);
    }
  }, [styleId, displayedOptions.length]);

  return (
    <fieldset className={cn({ [css.isHydraCantFindYourSize]: isHydraCantFindYourSize })}>
      <legend id={legendId} data-test-id={legendTestId} className={legendClassName}>
        <span className={css.sizeOptionsLegendText}>{legendText}</span> {sizingUnitPicker && makeSizingUnitPicker(sizingUnitPicker)}
      </legend>
      {makeOptions(displayedOptions, props, testId)}
    </fieldset>
  );
}
